import React, { Fragment, FunctionComponent, useCallback, useEffect } from "react";
import styles from "./MenuOverlay.module.scss";
import { AnchorMenuLink } from "../../Header4Props.interface";

import { CLOSE_ALL_OVERLAYS } from "layout/redux/overlay.actions";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import isBrowserContext from "helpers/isBrowserContext";
import SocialLinks from "../SocialLinks/SocialLinks";
import Button from "components/Button/Button";
import { useDispatch } from "react-redux";
import LanguageSelector from "layout/Header/blocks/Header1/components/LanguageSelector/LanguageSelector";

const MenuOverlay: FunctionComponent<any> = (props) => {
    const dispatch = useDispatch();

    const logoTypeClass = props.header?.logoType ? styles[`logo_type_${props.header.logoType}`] : undefined;
    const logoImageType = props.header?.logoType === "Vertical" ? "containX" : "containY";
    const imageAspectRatio = getImageAspectRatio(undefined, props.header?.logo?.umbracoWidth, props.header?.logo?.umbracoHeight);

    const headerButtonContentType =
        props.header?.headerButtons?.[0] && props.header.headerButtons.length > 0 ? props.header.headerButtons[0].content._contentType : undefined;

    const onEscKeyInput = useCallback((event) => {
        if (event.keyCode === 27) {
            dispatch({
                type: CLOSE_ALL_OVERLAYS,
            });
        }
    }, []);

    const onCloseButtonClick = useCallback((event) => {
        dispatch({
            type: CLOSE_ALL_OVERLAYS,
        });
    }, []);

    useEffect(() => {
        if (isBrowserContext()) {
            document.addEventListener("keydown", onEscKeyInput, false);
        }

        return () => {
            if (isBrowserContext()) {
                document.removeEventListener("keydown", onEscKeyInput, false);
            }
        };
    }, []);

    const onAnchorClick = (anchor: AnchorMenuLink) => {
        const identifier = anchor.label.replace(/\s+/g, "-").toLowerCase();

        if (isBrowserContext()) {
            const blockEl = document.getElementById(identifier);

            if (blockEl && typeof blockEl.offsetTop !== "undefined") {
                dispatch({
                    type: CLOSE_ALL_OVERLAYS,
                });

                window.scrollTo({
                    top: blockEl?.offsetTop - 50,
                    left: 0,
                    behavior: "smooth",
                });
            }
        }
    };

    return (
        <Fragment>
            {/* Lock the scrollbar for the rest of the page on mobile, while drawer is open */}
            {/* TODO: Remember the scroll position and restore it when toggling locked state */}
            <nav className={styles.wrapper}>
                <div className={combineClasses(styles.inner, logoTypeClass)}>
                    <div className={styles.header}>
                        {props.header?.logo ? (
                            <Anchor
                                className={styles.image_link}
                                to={props.page?._homeNodeUrl}
                            >
                                <ImageViewer
                                    className={styles.image}
                                    src={props.header.logo.umbracoFile.src}
                                    extensionType="png"
                                    type={logoImageType}
                                    aspectRatioX={imageAspectRatio.aspectRatioX}
                                    aspectRatioY={imageAspectRatio.aspectRatioY}
                                />
                            </Anchor>
                        ) : null}

                        <button
                            className={styles.menu_close_button}
                            onClick={(event) => onCloseButtonClick(event)}
                        >
                            <span></span>
                            <span></span>
                        </button>
                    </div>

                    <div className={styles.links}>
                        {props.anchors &&
                            props.anchors.map((anchor: any, index: number) => {
                                return (
                                    <div
                                        className={styles.link}
                                        key={index}
                                        onClick={() => onAnchorClick(anchor)}
                                    >
                                        <span className={styles.link_label}>{anchor.label}</span>
                                    </div>
                                );
                            })}
                    </div>

                    <div className={styles.buttons}>
                        {/* Buttons */}
                        {props.headerButtons?.map((button: any, index: number) => {
                            return (
                                <Button
                                    key={index}
                                    to={button.content.link}
                                    className={styles.action_button}
                                    contentType={headerButtonContentType}
                                    {...button.content}
                                >
                                    {button.content.label}
                                </Button>
                            );
                        })}

                        <div className={styles.buttons_social}>
                            {/* Social Links */}
                            <SocialLinks {...props} />

                            {/* Language Selector */}
                            {props?._cultures && Object.keys(props._cultures).length > 1 ? <LanguageSelector /> : null}
                        </div>
                    </div>
                </div>
            </nav>
        </Fragment>
    );
};

export default MenuOverlay;
